import type { Ingredient, IngredientRef } from '@recipes/contracts';
import { Input, Spinner, cn } from '@recipes/ui';
import { Link2, Plus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components/feedback/toaster';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import { offlineDb } from '@/lib/offline/db';
import { notifyCatalog, toIngredientRow } from './device-copy';
import { resolveIngredientLocally, searchIngredientsLocally } from './lookup';

export type PickedIngredient = Pick<IngredientRef, 'id' | 'name'>;

type Option =
  { kind: 'ingredient'; ingredient: PickedIngredient; own: boolean } | { kind: 'create' };

/**
 * "Which ingredient is this product?" – a text field that suggests ingredients from the device
 * copy while typing (so it works in the store), links a name that matches exactly by itself, and
 * offers to create the ingredient when there is none (that one needs a connection).
 */
export function IngredientCombobox({
  id,
  initial,
  onChange,
  placeholder,
}: {
  id: string;
  initial: PickedIngredient | null;
  onChange: (ingredient: PickedIngredient | null) => void;
  placeholder?: string;
}) {
  const { t } = useTranslation();
  const online = useOnlineStatus();
  const [text, setText] = useState(initial?.name ?? '');
  const [ingredient, setIngredient] = useState<PickedIngredient | null>(initial);
  const [suggestions, setSuggestions] = useState<Ingredient[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [creating, setCreating] = useState(false);
  // A suggestion that was chosen stays chosen while its name stands in the field – two
  // ingredients may share a name, and the one that was picked is the one that is meant.
  const picked = useRef<PickedIngredient | null>(initial);
  const report = useRef(onChange);
  report.current = onChange;

  const choose = (next: PickedIngredient | null) => {
    setIngredient(next);
    report.current(next);
  };

  useEffect(() => {
    let cancelled = false;
    const typed = text.trim();
    if (!typed) {
      setSuggestions([]);
      picked.current = null;
      choose(null);
      return;
    }
    const timer = setTimeout(() => {
      void Promise.all([resolveIngredientLocally(typed), searchIngredientsLocally(typed)]).then(
        ([exact, found]) => {
          if (cancelled) return;
          setSuggestions(found);
          if (picked.current && picked.current.name === typed) return;
          picked.current = null;
          choose(exact ? { id: exact.id, name: exact.name } : null);
        },
      );
    }, 150);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [text]);

  const typed = text.trim();
  const options: Option[] = [
    ...suggestions.map((s) => ({
      kind: 'ingredient' as const,
      ingredient: { id: s.id, name: s.name },
      own: s.scope !== 'GLOBAL',
    })),
    // Only when nothing carries exactly this name yet.
    ...(typed && !ingredient && online ? [{ kind: 'create' as const }] : []),
  ];
  const shown = open && options.length > 0;
  const current = Math.min(active, Math.max(0, options.length - 1));

  const pick = (next: PickedIngredient) => {
    picked.current = next;
    setText(next.name);
    choose(next);
    setOpen(false);
  };

  const create = async () => {
    if (!typed || creating) return;
    setCreating(true);
    try {
      const created = await api.ingredients.create({ name: typed });
      await offlineDb.catalogIngredients.put(toIngredientRow(created));
      notifyCatalog();
      toast.success(t('catalog.ingredients.created'));
      pick({ id: created.id, name: created.name });
    } catch (error) {
      toastError(error);
    } finally {
      setCreating(false);
    }
  };

  const select = (option: Option | undefined) => {
    if (!option) return;
    if (option.kind === 'create') void create();
    else pick(option.ingredient);
  };

  return (
    <div className="relative">
      <Input
        id={id}
        className="mt-1"
        role="combobox"
        aria-expanded={shown}
        aria-controls={`${id}-options`}
        aria-autocomplete="list"
        aria-activedescendant={shown ? `${id}-option-${current}` : undefined}
        autoComplete="off"
        maxLength={120}
        value={text}
        placeholder={placeholder}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (!shown) return setOpen(true);
            const step = e.key === 'ArrowDown' ? 1 : -1;
            setActive((current + step + options.length) % options.length);
          } else if (e.key === 'Enter' && shown) {
            // Choosing a suggestion must not send the form.
            e.preventDefault();
            select(options[current]);
          } else if (e.key === 'Escape' && shown) {
            e.stopPropagation();
            setOpen(false);
          }
        }}
        data-testid="product-ingredient"
      />
      {shown && (
        <ul
          id={`${id}-options`}
          role="listbox"
          aria-label={t('catalog.product.ingredientSuggestions')}
          className="absolute inset-x-0 z-20 mt-1 max-h-64 overflow-y-auto rounded-md border bg-card py-1 text-card-foreground shadow-md"
          data-testid="ingredient-options"
        >
          {options.map((option, index) => (
            <li
              key={option.kind === 'create' ? 'create' : option.ingredient.id}
              id={`${id}-option-${index}`}
              role="option"
              aria-selected={index === current}
              className={cn(
                'flex min-h-[40px] cursor-pointer items-center gap-2 px-3 py-1.5 text-sm',
                index === current && 'bg-accent text-accent-foreground',
                option.kind === 'create' && 'border-t font-medium',
              )}
              // The field keeps the focus, so the list is still there for the click.
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(index)}
              onClick={() => select(option)}
              data-testid={option.kind === 'create' ? 'ingredient-create' : 'ingredient-option'}
            >
              {option.kind === 'create' ? (
                <>
                  {creating ? (
                    <Spinner className="size-4" label={t('app.loading')} />
                  ) : (
                    <Plus className="size-4" aria-hidden />
                  )}
                  {t('catalog.product.ingredientCreate', { name: typed })}
                </>
              ) : (
                <>
                  <span className="min-w-0 flex-1 truncate">{option.ingredient.name}</span>
                  {option.own && (
                    <span className="text-xs text-muted-foreground">{t('catalog.own')}</span>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {typed && (
        <p
          className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"
          aria-live="polite"
          data-testid="product-ingredient-state"
        >
          <Link2 className="size-3 shrink-0" aria-hidden />
          {ingredient
            ? t('catalog.product.ingredientLinked', { name: ingredient.name })
            : online
              ? t('catalog.product.ingredientUnknownCreate')
              : t('catalog.product.ingredientUnknown')}
        </p>
      )}
    </div>
  );
}
